import React from 'react';

import { TableCell, TableRow, Grid, Typography, Paper } from '@mui/material';
import _ from 'lodash';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { type DashboardClassSessionRecord } from '../../../../../components/templates/types';
import { fromRedToGreen } from '../../../utils/colors';

const GridTimeItem = styled(Grid)`
    text-align: center;
    display: flex;
    flex-direction: column;
    padding: 12px;
    align-items: center;
`;

const TimeRowTitleText = styled(Typography)`
    display: inline;
    font-family: Lato;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px;
    letter-spacing: 0.15000000596046448px;
    text-align: center;
    color: #3a3c41;
    margin-left: 2px;
`;

const TimeRowTitle = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const PaperCircleText = styled(Typography)`
    font-family: Lato;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0px;
    text-align: center;
    color: #ffffff;
`;

const PaperCircle = styled(({ circleColor, ...otherProps }) => <Paper {...otherProps} />)`
    margin-top: 2px;
    border-radius: 50%;
    width: 45px;
    height: 45px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${(props) => props.circleColor};
`;

const renderTimeValueInCircle = (timeString: string) => {
    if (timeString.includes(', ')) {
        const times = timeString.split(', ');
        return (
            <>
                <div>{times[0]},</div>
                <div>{times[1]}</div>
            </>
        );
    }
    return timeString;
};

const TimeRowItem = ({ titleText, value, circleColor }) => (
    <GridTimeItem item xs={6} sm={6} md={4}>
        <TimeRowTitle>
            <TimeRowTitleText variant="h2">{titleText}</TimeRowTitleText>
        </TimeRowTitle>
        <PaperCircle elevation={4} circleColor={circleColor}>
            <PaperCircleText data-testid="value">{renderTimeValueInCircle(String(value))}</PaperCircleText>
        </PaperCircle>
    </GridTimeItem>
);

const StyledTableCell = styled(TableCell)`
    vertical-align: top;
    text-align: left;
`;

const ClassLink = styled(Link)`
    color: ${(props) => props.theme.colors.primary.main};
`;

interface RowProps {
    classSession: DashboardClassSessionRecord;
}

const ClassSessionTableRow: React.FC<RowProps> = ({ classSession }) => {
    const { t } = useTranslation('portal');
    console.log('classSession', classSession);
    const { start_time: startTime, class_session_uuid: classSessionUuid } = classSession;

    const tabLaunches: string[] = [];
    _.each(classSession!.feature_usage, (val, key) => {
        if (val !== 0) {
            if (key.indexOf('tab_type_') === 0) {
                tabLaunches.push(`${val} ${t(`${key.replace('tab_type_', '')}-event-name`)}`);
            }
        }
    });
    return (
        <>
            <TableRow>
                <StyledTableCell
                    style={{
                        width: '350px',
                        paddingTop: '26px'
                    }}
                >
                    <ClassLink to={`/portal/class-sessions/${classSessionUuid}`}>
                        {Moment(new Date(startTime * 1000)).format('MMMM Do YYYY')}
                    </ClassLink>
                </StyledTableCell>
                <StyledTableCell style={{ padding: '0px' }}>
                    {classSession.feature_usage!.user_count !== 0 && (
                        <TimeRowItem
                            titleText={''}
                            value={classSession.feature_usage!.user_count}
                            circleColor={fromRedToGreen(0 / 2)}
                        />
                    )}
                </StyledTableCell>
                <StyledTableCell style={{ padding: '0px' }}>
                    {classSession.feature_usage!.talk_time_seconds !== 0 && (
                        <TimeRowItem
                            titleText={''}
                            value={`${Math.round(Number(classSession.feature_usage!.talk_time_seconds) / 60)} min`}
                            circleColor={fromRedToGreen(1 / 2)}
                        />
                    )}
                </StyledTableCell>
                <StyledTableCell style={{ padding: '0px' }}>
                    {Number(classSession.feature_usage!.private_chat_count) +
                        Number(classSession.feature_usage!.breakout_chat_count) +
                        Number(classSession.feature_usage!.public_chat_count) !==
                        0 && (
                        <TimeRowItem
                            titleText={''}
                            value={
                                Number(classSession.feature_usage!.private_chat_count) +
                                Number(classSession.feature_usage!.breakout_chat_count) +
                                Number(classSession.feature_usage!.public_chat_count)
                            }
                            circleColor={fromRedToGreen(2 / 2)}
                        />
                    )}
                </StyledTableCell>
                <StyledTableCell style={{ paddingTop: '26px' }}>{tabLaunches.join(', ')}</StyledTableCell>
            </TableRow>
        </>
    );
};

export default ClassSessionTableRow;
